import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { Box, InputLabel, Stack, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { trim } from 'lodash';
import { MouseEvent, useContext, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { Breadcrumbs } from 'src/components/breadcrumbs';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { FullGcfComponentSection } from 'src/components/gcfComponentSection';
import { SimpleDialog } from 'src/components/modal';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { Snackbar } from 'src/components/snackbar';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import { useMenu, useModal } from 'src/hooks';
import {
  useClone,
  useDeleteComponent,
  useGetComponents,
  useUpdateComponent,
} from 'src/hooks/api';
import { useGetAllComponentTags } from 'src/hooks/api/componentTags/useGetAllComponentTags';
import { useApiErrorNavigator } from 'src/hooks/navigation';
import { ComponentTypes, IStatus, IStatusLabel } from 'src/interfaces';
import {
  DataTestID,
  generateAssemblyProductFilters,
  getComponentDisplayName,
  getExpiryStatusMessage,
  lang,
  normalizeComponentResponse,
  showSoftSellComponent,
  useUserRole,
} from 'src/utils';
import { getSectionPreviewData } from 'src/utils/gcf-helper';
import { componentConvertToDraftFromInfoEvent } from 'src/utils/google-tag-manager';

import { ComponentMenu } from '../listComponentsPage/modules/componentMenu/ComponentMenu';
import { TabPanelProps } from './DetailComponentPage.interface';
import {
  AntTab,
  AntTabs,
  BoxStyle,
  CardMediaContainer,
  ChipContainer,
  ComponentChipStyle,
  ComponentEditContainer,
  ComponentEditContentContainer,
  ComponentEditSideMenuContainer,
  ComponentInfoText,
  ComponentInfoTitle,
  ComponentMenuIcon,
  ComponentText,
  HeaderContainer,
  StyledChipContainer,
  StyledDynamicTooltipChip,
} from './DetailComponentPage.styled';

export const DetailComponentPage = () => {
  const isDeleted = useRef(false);
  const params = useParams();
  const { componentId = '' } = params;

  const { data, error } = useGetComponents(
    componentId ? [`id.list=${componentId}`] : [],
    {
      enabled: !isDeleted.current,
    },
  );

  const componentDetails = normalizeComponentResponse(data);

  const menu = useMenu();
  const deleteComponentModal = useModal();
  const duplicateComponentModal = useModal();
  const convertToDraftModal = useModal();
  const deleteComponent = useDeleteComponent();
  const updateComponent = useUpdateComponent();
  const cloneResource = useClone();
  const navigate = useNavigate();
  const location = useLocation();

  const [duplicatedComponentName, setDuplicatedComponentName] = useState('');

  const isExpiringSoon = getExpiryStatusMessage(componentDetails?.end_date);
  const { isViewOnly, isOwnerOperator } = useUserRole();
  const { userRole } = useContext(AuthDataProvider);

  const { data: selectedChipTags = [] } = useGetAllComponentTags(
    userRole,
    [`id.list=${(componentDetails?.component_tag_id ?? []).join('|')}`],
    Boolean((componentDetails?.component_tag_id?.length ?? 0) > 0),
  );

  const { productFilters, productVariationFilters } = useMemo(() => {
    if (!componentDetails) {
      return { productFilters: [], productVariationFilters: [] };
    }

    return generateAssemblyProductFilters([], [componentDetails]);
  }, [componentDetails]);

  useApiErrorNavigator(error);

  const onMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    menu.onOpenMenu(event.currentTarget);
  };
  const onHandleConvertToDraftModal = async (
    componentId: number,
    componentName: string,
  ) => {
    try {
      let payload = {
        id: componentId,
        status: IStatus.DRAFT,
      };
      await updateComponent.mutateAsync(payload);
      convertToDraftModal.onCloseModal();
      Snackbar.success(`${componentName} ${lang.successfullyConvertedToDraft}`);
      componentConvertToDraftFromInfoEvent(componentId);

      if (location.pathname.includes(PATHS.COMPONENT_DETAILS()))
        navigate(PATHS.COMPONENTS);
    } catch {
      Snackbar.error(lang.failedToConvert);
    }
  };

  const onHandleDeleteComponentModal = async (
    componentId: number,
    componentName: string,
  ) => {
    try {
      isDeleted.current = true;
      await deleteComponent.mutateAsync({ componentId, componentName });
      navigate(PATHS.COMPONENT_DETAILS());
      deleteComponentModal.onCloseModal();
      Snackbar.success(`${componentName} ${lang.successfullyDeleted}`);
      if (location.pathname.includes(PATHS.COMPONENT_DETAILS()))
        navigate(PATHS.COMPONENTS);
    } catch {
      isDeleted.current = false;
      Snackbar.error(lang.failedToDeleteComponent);
    }
  };

  const handleDuplicateComponentsConfirm = async () => {
    if (!componentDetails) return;

    await cloneResource.mutateAsync({
      type: 'component',
      id: componentDetails.id,
      name: trim(duplicatedComponentName),
    });

    Snackbar.success(`${componentDetails.name} ${lang.successfullyDuplicated}`);
    setDuplicatedComponentName('');
    duplicateComponentModal.onCloseModal();
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}>
        {value === index && <BoxStyle>{children}</BoxStyle>}
      </div>
    );
  };

  // TODO: redirect to 404 page
  if (!componentId || !componentDetails) return null;

  const event = dayjs(componentDetails.created_at);
  const createdAtDate = event.format('MM-DD-YYYY');

  return (
    <ComponentEditContainer>
      <ComponentEditSideMenuContainer>
        <Box>
          <AntTabs value={0} aria-label="component details tabs">
            <AntTab label="Details" {...a11yProps(0)} />
          </AntTabs>
          <Box />
        </Box>
        <TabPanel value={0} index={0}>
          <ComponentInfoTitle>{lang.dateCreated}</ComponentInfoTitle>
          <ComponentInfoText>{createdAtDate}</ComponentInfoText>
          <ComponentInfoTitle>{lang.status}</ComponentInfoTitle>
          <ChipContainer>
            <ComponentChipStyle>
              {isExpiringSoon && componentDetails.status === IStatus.READY ? (
                <DynamicTooltipChip
                  chipName={isExpiringSoon}
                  color="error"
                  variant="outlined"
                  size="small"
                />
              ) : (
                <DynamicTooltipChip
                  chipName={
                    componentDetails.status
                      ? IStatusLabel[`${componentDetails.status}`]
                      : componentDetails.status
                  }
                  color={
                    componentDetails.status === IStatus.DRAFT
                      ? 'primary'
                      : 'success'
                  }
                  variant="outlined"
                  size="small"
                />
              )}
            </ComponentChipStyle>
            <ComponentChipStyle>
              {showSoftSellComponent(componentDetails) && (
                <DynamicTooltipChip
                  chipName={lang.softSell}
                  color="info"
                  variant="outlined"
                  size="small"
                />
              )}
            </ComponentChipStyle>
          </ChipContainer>
          <ComponentInfoTitle>{lang.componentType} </ComponentInfoTitle>
          <ComponentInfoText>
            {getComponentDisplayName(
              componentDetails.component_type?.name as ComponentTypes,
            )}
          </ComponentInfoText>
          <ComponentInfoTitle>{lang.size}</ComponentInfoTitle>
          <ComponentInfoText>
            {componentDetails?.width} x {componentDetails?.height} (
            {lang.pixels})
          </ComponentInfoText>
          {selectedChipTags.length > 0 && (
            <>
              <ComponentInfoTitle>{lang.tags}</ComponentInfoTitle>
              <Box overflow={'auto'} maxHeight={190}>
                {selectedChipTags.map((tag) => (
                  <StyledChipContainer>
                    <StyledDynamicTooltipChip
                      key={tag.id}
                      chipName={tag?.name}
                      tooltipTitle={tag?.name}
                      tooltipWidth={500}
                      tooltipActivationWidth={252}
                    />
                  </StyledChipContainer>
                ))}
              </Box>
            </>
          )}
        </TabPanel>
      </ComponentEditSideMenuContainer>
      <ComponentEditContentContainer>
        <HeaderContainer>
          <Box overflow={'hidden'}>
            {!isOwnerOperator && (
              <Breadcrumbs
                items={[
                  {
                    label: lang.components,
                    link: PATHS.COMPONENTS,
                  },
                  {
                    label: lang.detail,
                  },
                ]}
              />
            )}

            <CustomTooltip
              width={500}
              placement="bottom"
              title={componentDetails?.name}
              overflowLineNumber={1}>
              <ComponentText variant="h3" component="h1">
                {componentDetails?.name}
              </ComponentText>
            </CustomTooltip>
          </Box>
          <Stack spacing={2} direction="row">
            <Box>
              <CardMediaContainer>
                <Stack spacing={1.5} direction="row" alignItems="center">
                  {isViewOnly && <ViewOnlyAlert />}
                  {!isOwnerOperator && (
                    <>
                      <ComponentMenuIcon
                        data-testid={DataTestID.DetailComponentMenuIcon}
                        onClick={onMenuClick}>
                        <MoreHorizIcon />
                      </ComponentMenuIcon>
                    </>
                  )}
                </Stack>
              </CardMediaContainer>

              <ComponentMenu
                anchorEl={menu.anchorEl}
                onClose={menu.onCloseMenu}
                card={componentDetails}
                onDeleteClick={deleteComponentModal.onOpenModal}
                onDuplicateClick={duplicateComponentModal.onOpenModal}
                onConvertDraftClick={convertToDraftModal.onOpenModal}
              />
              {componentDetails && (
                <>
                  <SimpleDialog
                    open={deleteComponentModal.open}
                    cancelText={lang.cancel}
                    confirmText={lang.deleteText}
                    title={lang.DeleteComponentModal.title}
                    subTitle={lang.DeleteComponentModal.subTitle}
                    onClose={deleteComponentModal.onCloseModal}
                    onClickCancel={deleteComponentModal.onCloseModal}
                    modalSize="sm"
                    disabled={isDeleted.current}
                    onClickConfirm={() =>
                      onHandleDeleteComponentModal(
                        componentDetails.id,
                        componentDetails.name,
                      )
                    }
                  />

                  <SimpleDialog
                    open={convertToDraftModal.open}
                    cancelText={lang.cancel}
                    confirmText={lang.confirm}
                    title={lang.convertToDraftComponentModal.title}
                    subTitle={lang.convertToDraftComponentModal.subTitle}
                    onClose={convertToDraftModal.onCloseModal}
                    onClickCancel={convertToDraftModal.onCloseModal}
                    modalSize="sm"
                    onClickConfirm={() =>
                      onHandleConvertToDraftModal(
                        componentDetails.id,
                        componentDetails.name,
                      )
                    }
                  />

                  {duplicateComponentModal.open && (
                    <SimpleDialog
                      title={lang.duplicateComponent}
                      open={duplicateComponentModal.open}
                      confirmText={lang.create}
                      isLoading={cloneResource.isLoading}
                      disabled={
                        !trim(duplicatedComponentName) ||
                        cloneResource.isLoading
                      }
                      onClose={duplicateComponentModal.onCloseModal}
                      onClickCancel={duplicateComponentModal.onCloseModal}
                      onClickConfirm={handleDuplicateComponentsConfirm}>
                      <>
                        <InputLabel>{lang.componentName}</InputLabel>
                        <TextField
                          inputProps={{
                            'data-testid':
                              DataTestID.DetailComponentDuplicateName,
                          }}
                          fullWidth
                          onChange={(event) => {
                            setDuplicatedComponentName(event.target.value);
                          }}
                        />
                      </>
                    </SimpleDialog>
                  )}
                </>
              )}
            </Box>
          </Stack>
        </HeaderContainer>
        <ScaleContainer enableScaleControls>
          <FullGcfComponentSection
            assemblyComponents={[
              {
                component: {
                  type: componentDetails?.component_type?.name,
                  config: componentDetails.config,
                  name: componentDetails?.name,
                  width: componentDetails?.width,
                  height: componentDetails?.height,
                  theme: componentDetails?.theme?.name,
                },
              },
            ]}
            height={componentDetails?.height || 0}
            width={componentDetails?.width || 0}
            previewData={getSectionPreviewData(
              componentDetails?.component_type?.name as ComponentTypes,
            )}
            containerStyle={{
              borderWidth: '3px',
              borderStyle: 'solid',
              borderColor: 'brand.darkGrey',
            }}
            overrideQuerySetting={{
              staleTime: Infinity,
            }}
            productFilters={productFilters}
            productVariationFilters={productVariationFilters}
            skipProductsApiOnEmptyFilters={
              !(productFilters.length || productVariationFilters.length)
            }
          />
        </ScaleContainer>
      </ComponentEditContentContainer>
    </ComponentEditContainer>
  );
};
